import { GridColDef } from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { NESTED_ROUTES } from 'common/src/routes/BaseRoutes';
import { IAccount, IPage, IUtility } from 'common/src/types/interfaces';

import { QUERY_KEYS } from 'portal/src/api/constants';
import { getUtilities, updateUtility } from 'portal/src/api/UtilitiesService';
import type { DeactivateConfig } from 'portal/src/components/common/CustomToolbar';
import DataTable from 'portal/src/components/common/DataTable';
import addBooleanColumnProps from 'portal/src/components/common/tables/addBooleanColumnProps';
import addNumberColumnProps from 'portal/src/components/common/tables/addNumberColumnProps';
import { NEW_ID } from 'portal/src/constants/ReservedIDs';
import { isActiveAccount, isActiveMeter } from 'portal/src/utils/checkActive';

const Utilities = () => {
  const navigate = useNavigate();

  const [showDeactivatedUtilities, setShowDeactivatedUtilities] =
    useState(false);

  const {
    data: utilities,
    isFetched,
    isError,
    refetch,
  } = useQuery({
    queryKey: [QUERY_KEYS.utilities],
    queryFn: async () => await getUtilities(),
    initialData: { data: [] } as IPage<IUtility>,
  });

  const handleNavigate = (id: string) => navigate(`./${id}`);

  const deactivateConfig: DeactivateConfig = {
    isChecked: showDeactivatedUtilities,
    onToggleCheckbox: setShowDeactivatedUtilities,
  };

  const gridColumns: GridColDef[] = [
    { field: 'name', headerName: 'Utility Name', flex: 1 },
    {
      field: 'serviceTypes',
      headerName: 'Service Type(s)',
      flex: 0.5,
      minWidth: 120,
      valueGetter: (serviceTypes: []) => {
        const services = serviceTypes?.map(
          (serviceType: string) => serviceType,
        );
        return services?.join(', ');
      },
    },
    {
      field: 'vendorNumber',
      headerName: 'Vendor Number',
      flex: 0.5,
      minWidth: 130,
    },
    {
      field: 'accounts',
      valueGetter: (accounts: IAccount[]) =>
        (accounts || []).filter((a) => isActiveAccount(a)).length,
      ...addNumberColumnProps('Active Accounts', true, 200),
    },
    {
      field: 'meters',
      valueGetter: (_, row: IUtility) => {
        const allMeters = (row.accounts || []).flatMap(
          (account) => account.meters || [],
        );
        const uniqueMeters = [
          ...new Map(allMeters.map((m) => [m.id, m])).values(),
        ];
        return uniqueMeters.filter((m) => isActiveMeter(m)).length;
      },
      ...addNumberColumnProps('Active Meters', true, 200),
    },
    {
      field: 'isActive',
      headerName: 'Deactivated',
      width: 112,
      ...addBooleanColumnProps({
        displayLogic: { valueForYes: false, yesIsGreen: false },
        includeTooltip: true,
      }),
    },
  ];

  return (
    <DataTable
      actionsConfig={{
        readActionConfig: {
          onClick: (id) => handleNavigate(id),
        },
        changeActiveConfig: {
          targetStr: 'utility',
          onChangeActiveStatus: async (id, isActive) => {
            await updateUtility(id, { isActive: !isActive });
            await refetch();
          },
          snackbarMsgKey: 'UTILITY',
        },
      }}
      columns={gridColumns}
      contextName={NESTED_ROUTES.ADMINISTRATION.UTILITIES}
      dataFetchError={isError}
      loading={!isFetched}
      rows={
        showDeactivatedUtilities
          ? utilities.data
          : utilities.data.filter(({ isActive }) => isActive)
      }
      toolbarSlotProps={{
        deactivateConfig,
        exportFileName: 'utilities',
        onNewButtonClick: () => handleNavigate(NEW_ID),
        showFilters: true,
      }}
    />
  );
};

export default Utilities;
